import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../../lib/http";
import KeyboardSpacer from "../../shared/components/KeyboardSpacer";
import PublicFormShell from "../../shared/components/PublicFormShell";
import PublicFormSkeleton from "../../shared/components/PublicFormSkeleton";
import PublicFormSplash, { useSplash } from "../../shared/components/PublicFormSplash";
import {
  StepActions,
  StepHeading,
  StepProgress,
} from "../components/PreconsultationChrome";
import PreconsultationReview from "../components/PreconsultationReview";
import { PreconsultationBasics } from "../components/PreconsultationSummary";
import PreconsultationSubmitted from "../components/PreconsultationSubmitted";
import PreconsultationWelcome, {
  PreconsultationWelcomeAction,
} from "../components/PreconsultationWelcome";
import {
  BasicsStep,
  BodyStep,
  DayStep,
  FoodStep,
  HealthStep,
  LocationStep,
  controlId,
} from "../components/PreconsultationSteps";
import type { StepProps } from "../components/PreconsultationSteps";
import { usePreconsultationForm } from "../hooks/usePreconsultationForm";
import {
  DRAFT_SAVE_MS,
  clearPreconsultationDraft,
  readPreconsultationDraft,
  writePreconsultationDraft,
} from "../preconsultationDraft";
import type { PreconsultationDraft } from "../preconsultationDraft";
import { stepIndexOf, stepsAsked } from "../preconsultationSteps";
import type { PreconsultationStepId } from "../preconsultationSteps";
import {
  EMPTY_ANSWERS,
  validateStep,
} from "../preconsultationValidation";
import type {
  PreconsultationErrors,
  PreconsultationField,
} from "../preconsultationValidation";
import {
  isSpentLink,
  submitPreconsultation,
  toPreconsultationPayload,
} from "../services/preconsultationService";
import { FORMS_PREVIEW, formHandleFromHost } from "../../../lib/site/config";
import { usePublicFormHead } from "../../shared/publicFormHead";
import type { PublicBrand } from "../../enquiries/types/enquiries.types";
import type { PreconsultationAnswers } from "../types/preconsultation.types";

/**
 * The pre-consultation form, at `<practitioner>.naurist.com/i/<token>`.
 *
 * Nine screens behind one URL — a welcome, seven steps, a thank-you — and not
 * one of them is a route. Nothing here is a place anybody would link to or
 * come back to: the form is a single sitting against a single-use token, and a
 * URL per step would invite a reload that landed halfway through with every
 * answer gone.
 *
 * Renders with nobody signed in. No Clerk session, no workspace, no toasts, no
 * query client — see `App`, which mounts the forms site above all of it.
 * Anything imported here has to work without them.
 */

type Phase = "welcome" | "steps" | "submitted";

/** Stands in until the practitioner's own brand has been read. */
const NO_BRAND: PublicBrand = { name: "", logoUrl: "" };

/** Which component draws which step. The review step is not here — it takes
 *  different props, and reads every answer rather than a step's worth. */
const STEP_BODIES: Partial<
  Record<PreconsultationStepId, React.FC<StepProps>>
> = {
  basics: BasicsStep,
  location: LocationStep,
  body: BodyStep,
  food: FoodStep,
  day: DayStep,
  health: HealthStep,
};

/**
 * What a screen with nothing to show says.
 *
 * Never names the mechanism. A token that expired, one that was filled in
 * yesterday by somebody else, and one that never existed are the same message
 * — the state of a practitioner's account is not a stranger's business, and
 * confirming that a link was once real is itself telling them something.
 */
const Notice: React.FC<{
  title: string;
  detail: string;
  action?: { label: string; onClick: () => void };
}> = ({ title, detail, action }) => (
  <div className="sm:text-center">
    <h1 className="text-[26px] sm:text-[32px] font-semibold text-[#0A4A52]">
      {title}
    </h1>
    <p className="mt-3 text-[15px] leading-7 text-ink-muted">{detail}</p>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="mt-6 text-[14px] font-medium text-brand hover:underline"
      >
        {action.label}
      </button>
    )}
  </div>
);

const PreconsultationFormPage: React.FC = () => {
  const { token = "" } = useParams<{ token: string }>();
  // Whose form this is, off the address bar. The read is made under the
  // practitioner's slug as well as the token — the link is one address, and
  // `ayushisahuu.naurist.com/i/<token>` is the whole of what identifies it.
  const handle = formHandleFromHost();
  const { status, form, reload, markCompleted } = usePreconsultationForm(
    handle,
    token
  );

  // The practitioner's mark alone for the first three seconds, in front of all
  // of this — the enquiry form opens exactly the same way, and the two arrive
  // on the same phone from the same person. Cut short on the endings with no
  // form behind them: a splash in front of "this link is no longer valid" is
  // three seconds of blank white and then an apology.
  const splashing = useSplash(
    status === "missing" || status === "failed" || status === "completed"
  );

  const [phase, setPhase] = useState<Phase>("welcome");
  const [index, setIndex] = useState(0);
  // Whether the basics step is showing its questions rather than the card of
  // what is already on file. Only ever reached by pressing Edit on that card —
  // see `PreconsultationBasics`.
  const [editingBasics, setEditingBasics] = useState(false);
  const [answers, setAnswers] = useState<PreconsultationAnswers>(EMPTY_ANSWERS);
  // A field's rule only surfaces once it has been visited, so a step does not
  // open covered in red before anything has been typed into it.
  const [touched, setTouched] = useState<
    Partial<Record<PreconsultationField, boolean>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  // Set once the answers have been opened on what the workspace already knew,
  // so a later re-render of the same form cannot overwrite what has since been
  // typed into those boxes.
  const opened = useRef(false);

  const brand = form?.brand ?? NO_BRAND;

  /**
   * The steps this practitioner asks, which is not always all of them — see
   * `stepsAsked`. Memoised because the progress bar, the review screen and the
   * Edit links all index into it, and a new array each render would have them
   * re-measuring a list that has not changed.
   *
   * Every step while the form is still on its way, so the shape of the page
   * does not depend on a read that has not answered; nothing is drawn from it
   * until `form` is in anyway.
   */
  const steps = useMemo(
    () => (form ? stepsAsked(form.fields) : []),
    [form]
  );

  const step = steps[index];
  const onReview = step?.id === "review";
  const onBasics = step?.id === "basics";
  /** The basics step on the face it opens on: what is held, not what to type. */
  const reviewingBasics = onBasics && !editingBasics;

  // The tab and the social card belong to the practitioner rather than to us —
  // see `usePublicFormHead`, which also leaves the tab without Naurist's mark
  // on it.
  usePublicFormHead(brand.name, "Pre-consultation form");

  // What is already on file, put into the boxes it belongs in — and on top of
  // it, whatever this device still holds of a sitting that was interrupted.
  // Once only: this is the form opening on what is known, not a value the form
  // has to keep.
  //
  // The draft wins wherever the two disagree, and has to: everything in it was
  // typed on top of these same known values, so letting `known` land last
  // would put the practitioner's version of an email back over the correction
  // the client had just made to it.
  useEffect(() => {
    if (!form || opened.current) return;
    opened.current = true;

    const draft = readPreconsultationDraft(handle, token);
    setAnswers((current) => ({ ...current, ...form.known, ...draft?.answers }));
    if (!draft) return;

    // Straight back to where they were, past the greeting: somebody reopening
    // a form they are halfway through is not being welcomed to it again. The
    // step is clamped because the practitioner may have switched that section
    // off since — `stepIndexOf` answers -1 for a step this form no longer
    // asks, and the answers matter more than the place in the run.
    setPhase("steps");
    setIndex(Math.max(stepIndexOf(draft.step, steps), 0));
  }, [form, handle, token, steps]);

  /**
   * What would be written if this tab went away this instant, and `undefined`
   * where there is nothing worth keeping — before the form has loaded, and
   * after it has been sent. A ref, so the listener below is subscribed once
   * rather than torn down and rebuilt on every keystroke.
   */
  const pending = useRef<PreconsultationDraft | undefined>(undefined);

  // Every answer, kept on this device and sent nowhere. Debounced, so a step's
  // worth of typing is one write rather than one per character; `step` is in
  // the deps beside `answers`, so moving between steps saves at once.
  useEffect(() => {
    if (!form || phase !== "steps" || !step) {
      pending.current = undefined;
      return;
    }

    const draft: PreconsultationDraft = { answers, step: step.id };
    pending.current = draft;

    const timer = window.setTimeout(
      () => writePreconsultationDraft(handle, token, draft),
      DRAFT_SAVE_MS
    );
    return () => window.clearTimeout(timer);
  }, [answers, form, handle, phase, step, token]);

  // A phone does not close a tab, it takes it away — and iOS fires neither
  // `unload` nor `beforeunload` when it reclaims one. `pagehide` and the
  // moment the page turns hidden are what actually arrive, so the debounce
  // above is flushed on both rather than losing the last box typed into.
  useEffect(() => {
    const flush = () => {
      if (pending.current) {
        writePreconsultationDraft(handle, token, pending.current);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [handle, token]);

  // A link that has been spent or has run out is an ending: there is nothing
  // left to restore into, and what the draft holds is health answers that
  // should not sit on the device for nobody. Reached by this tab sending the
  // form as well — see the spent-link branch in `send`.
  useEffect(() => {
    if (status !== "completed" && status !== "missing") return;
    pending.current = undefined;
    clearPreconsultationDraft(handle, token);
  }, [status, handle, token]);

  // Every screen starts at its own top. Without this a short step reached from
  // the bottom of a long one opens already scrolled past its own heading, and
  // the reader has to work out what changed.
  //
  // The thank-you most of all, which is why this is not `phase === "steps"` any
  // more. Submit is pressed at the foot of the review screen, which is the
  // longest on the form — so the screen that replaced it opened at whatever
  // scroll the browser could still honour, with the practitioner's logo cut in
  // half above it. The last thing this form does should not be a page somebody
  // has to scroll up to read.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [phase, index]);

  // The basics step is left as it was found. Somebody who opened its fields,
  // changed a date and moved on has finished with them; coming back — by Back,
  // or by the review screen's Edit — should show what they now hold rather
  // than the boxes they last had open.
  useEffect(() => {
    if (!onBasics) setEditingBasics(false);
  }, [onBasics]);

  const errors: PreconsultationErrors = useMemo(
    () => (step ? validateStep(step.id, answers) : {}),
    [step, answers]
  );

  const set = (patch: Partial<PreconsultationAnswers>) =>
    setAnswers((current) => ({ ...current, ...patch }));

  const visit = (field: PreconsultationField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  /** Only the problems this reader has already had a chance to fix. */
  const visibleErrors: PreconsultationErrors = useMemo(() => {
    const shown: PreconsultationErrors = {};
    (Object.keys(errors) as PreconsultationField[]).forEach((field) => {
      if (touched[field]) shown[field] = errors[field];
    });
    return shown;
  }, [errors, touched]);

  /**
   * Takes the reader to the first thing this step is missing.
   *
   * Marking the fields red and leaving it there is not enough on a phone: the
   * failing one is very often above or below the fold, and a Continue that
   * does nothing visible reads as a broken button.
   */
  const showProblems = () => {
    const fields = Object.keys(errors) as PreconsultationField[];
    setTouched((current) => ({
      ...current,
      ...Object.fromEntries(fields.map((field) => [field, true])),
    }));

    const first = fields[0];
    if (!first) return;
    const control = document.getElementById(controlId(first));
    control?.scrollIntoView({ behavior: "smooth", block: "center" });
    control?.focus({ preventScroll: true });
  };

  const send = async () => {
    // Nothing to send without the form the catalogs came with: the payload is
    // built out of them — five of its fields are ids resolved against them —
    // and Submit is unreachable before the read has answered anyway.
    if (!form) return;

    setSubmitting(true);
    setSubmitError(undefined);

    try {
      // A preview build has no endpoint to post to — the same reason the read
      // is stubbed. Without this the thank-you screen would be unreachable.
      if (!FORMS_PREVIEW) {
        await submitPreconsultation(
          handle,
          token,
          toPreconsultationPayload(answers, form.catalogs)
        );
      }
      // Answered and gone up. The device's copy has done its job, and what it
      // holds is not something to leave behind on a phone.
      pending.current = undefined;
      clearPreconsultationDraft(handle, token);
      setPhase("submitted");
    } catch (error) {
      if (isSpentLink(error)) {
        // The link went between the form being opened and being sent — a
        // second tab, or a form finished twice. There is nothing to retry, so
        // the page stops being a form and says so.
        markCompleted();
        return;
      }
      console.error("Pre-consultation submit failed:", error);
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (submitting) return;

    if (Object.keys(errors).length > 0) {
      // The step's summary face draws no boxes, so there would be nothing to
      // point at: open its fields first. Only reachable at all through a date
      // of birth typed and then broken, which is the one answer on that step
      // the reader can get wrong.
      if (reviewingBasics) setEditingBasics(true);
      showProblems();
      return;
    }

    if (onReview) {
      void send();
      return;
    }

    setIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  /* ── The states with no form behind them ─────────────────────────────── */

  if (splashing) return <PublicFormSplash brand={brand} />;

  if (status === "loading") {
    return (
      // The welcome screen's own frame, to the prop — the same one the enquiry
      // form waits in, and for the same reason: this page always opens on the
      // greeting, so a skeleton wearing the form's frame drew a progress bar
      // and two grey cards on a tinted background, and then the read landed on
      // a white screen with a centred greeting. Everything moved and the
      // background changed colour under it. What arrives now lands where the
      // shimmer stood.
      <PublicFormShell
        brand={NO_BRAND}
        loading
        centered
        lockup="inline"
        gutter="wide"
        fit
      >
        <PublicFormSkeleton />
      </PublicFormShell>
    );
  }

  if (status === "missing") {
    return (
      <PublicFormShell brand={brand} centered>
        <Notice
          title="This link is no longer valid"
          detail="Please ask your nutritionist to send you a new one."
        />
      </PublicFormShell>
    );
  }

  if (status === "completed") {
    return (
      <PublicFormShell brand={brand} centered>
        <Notice
          title="This form has already been completed"
          detail="Thank you — there is nothing more to do here."
        />
      </PublicFormShell>
    );
  }

  if (status === "failed" || !form) {
    return (
      <PublicFormShell brand={NO_BRAND} centered>
        <Notice
          title="Something went wrong"
          detail="We could not reach the server. Please check your connection and try again."
          action={{ label: "Try again", onClick: reload }}
        />
      </PublicFormShell>
    );
  }

  /* ── The form ────────────────────────────────────────────────────────── */

  if (phase === "welcome") {
    return (
      // The mark travels with the greeting rather than sitting at the top of
      // the page: nothing else is on this screen, so the two belong together.
      // `fit`: a greeting, two sentences and a button are the whole of this
      // screen, so there is nowhere for a scroll to go and it does not get one
      // — not even the few pixels a phone's own toolbars would otherwise steal.
      <PublicFormShell
        brand={brand}
        centered
        lockup="inline"
        gutter="wide"
        fit
        // The one action on the screen, held at the bottom edge rather than
        // sitting under the words — which is where every Continue after it
        // lands, so the first press of the form is in the same place as all
        // the rest. See `footer` on the shell.
        footer={<PreconsultationWelcomeAction onBegin={() => setPhase("steps")} />}
      >
        {/* Their first name off the record, and failing that the name the
            form was raised against — which the backend sends whole, so it is
            cut to the first word rather than greeting somebody by both of
            theirs. A greeting is a first name or it is nothing. */}
        <PreconsultationWelcome
          name={form.known.firstName || form.recipientName.split(" ")[0]}
        />
      </PublicFormShell>
    );
  }

  if (phase === "submitted") {
    // Not `centered`: this screen places its own message and keeps the closing
    // line at the foot, which it can only do if the column leaves it the height.
    return (
      <PublicFormShell brand={brand}>
        <PreconsultationSubmitted />
      </PublicFormShell>
    );
  }

  const Body = STEP_BODIES[step.id];

  return (
    <PublicFormShell brand={brand} tone="form" width="narrow">
      {/* A flex column filling what the shell gives it, so the bar at the foot
          can be pushed to the bottom of the screen on a step that does not
          reach it — see the `flex-1` on the questions below. */}
      <form
        className="flex flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          next();
        }}
        noValidate
      >
        {/*
          What the step is, and how much of the form is left, on one line from
          `sm:` up — the name at the left of the column and the bar against its
          right edge, which is what the design draws.

          `flex-col-reverse` on a phone, where they are stacked the other way
          round: the bar goes above the heading there, since a reader who has
          just pressed Continue looks first for how much further there is to
          go. The heading stays first in the document either way, so it is
          still the first thing read out.

          The air above it is the desktop's. The mark sits at the top of the
          page on these screens rather than travelling with the content, and
          the design leaves a good deal of white between the two — without it
          the logo reads as part of the heading block rather than as the page's
          own.
        */}
        <div className="flex flex-col-reverse gap-5 sm:mt-14 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
          <StepHeading
            // Numbered by where it falls in what this practitioner asks, so it
            // says the same thing as the count on the bar beside it.
            step={index + 1}
            label={step.label}
            title={step.title}
          />
          <StepProgress index={index} total={steps.length} />
        </div>

        {/*
          Grows into whatever the step does not use, which is what holds the
          bar against the bottom edge of a phone whether the step is four
          questions or fourteen. The alternative — an auto margin on the bar
          itself — would collapse the gap above it the moment a step did fill
          the screen, and the glass would start at the card's own edge.

          On a wide screen too, where the design puts the action near the foot
          of the window rather than tucked under a card that ended a third of
          the way down. A step long enough to fill the page pushes it back
          under the last card by itself, which is the review screen and is what
          that screen's mockup shows — one rule, both cases.
        */}
        <div className="mt-5 flex-1 space-y-4 sm:mt-8">
          {onReview ? (
            <PreconsultationReview
              answers={answers}
              // Only the sections this form actually asked. A card of dashes
              // for questions nobody was put would read as answers somebody
              // failed to give.
              steps={steps}
              onEdit={(id) => setIndex(stepIndexOf(id, steps))}
            />
          ) : reviewingBasics ? (
            // Nothing to fill in: their practitioner already did. Continue
            // takes it as read and goes on to the next step.
            <PreconsultationBasics
              answers={answers}
              onEdit={() => setEditingBasics(true)}
            />
          ) : (
            Body && (
              <Body
                answers={answers}
                set={set}
                errors={visibleErrors}
                visit={visit}
                catalogs={form.catalogs}
                fields={form.fields}
              />
            )
          )}
        </div>

        {/* Above the bar rather than inside it: it is about the submission
            that failed, not about the button, and a message that pushed the
            button down as it arrived would move the thumb's target. */}
        {submitError && (
          <p
            role="alert"
            className="mt-4 rounded-[8px] border border-[#F3C9C9] bg-[#FDECEC] px-4 py-3 text-[13px] leading-5 text-[#B23B3B]"
          >
            {submitError}
          </p>
        )}

        {/* Keeps the bar below sliding all the way to the bottom edge while a
            keyboard is up, instead of stopping over the field being typed
            into — see `KeyboardSpacer`. Nothing at all otherwise. */}
        <KeyboardSpacer />

        {/* The bar's Continue is this form's submit button — see
            `StepActions`, which is why it takes no handler of its own. */}
        <StepActions
          // Out of the basics step's own fields and back to the card they were
          // opened from, where that is where Back leads — on the first step
          // there is no earlier step to go to, but there is a screen behind.
          onBack={
            index > 0
              ? () => setIndex(index - 1)
              : editingBasics
              ? () => setEditingBasics(false)
              : undefined
          }
          nextLabel={onReview ? "Submit" : "Continue"}
          busy={submitting}
        />
      </form>
    </PublicFormShell>
  );
};

export default PreconsultationFormPage;
